/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { Event } from '../../../../common/types/index'
import { paragraphXSmall, weightRegular } from '../css'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Text from '../ts-components/Text'
import { useState, useCallback } from 'react'
import AdditionalInfoModal from './AdditionalInfoModal'

// Helper function to truncate HTML content while preserving formatting
const truncateHtmlContent = (
  html: string,
  maxLength: number = 200
): { truncated: string; isTruncated: boolean } => {
  // Clean up excessive whitespace and empty lines
  let cleanedHtml = html
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br> tags with spaces
    .replace(/\s*<\/p>\s*<p>\s*/gi, '</p><p>') // Remove whitespace between paragraphs
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/>\s+</g, '><') // Remove whitespace between HTML tags
    .trim()

  // Remove HTML tags to check text length
  const strippedText = cleanedHtml.replace(/<[^>]*>/g, '').trim()

  // Check if truncation is needed
  if (strippedText.length <= maxLength) {
    return { truncated: cleanedHtml, isTruncated: false }
  }

  // Find the truncation point in the original HTML
  let textLength = 0
  let truncatedHtml = ''
  let inTag = false

  for (let i = 0; i < cleanedHtml.length; i++) {
    const char = cleanedHtml[i]

    if (char === '<') {
      inTag = true
    }

    if (!inTag) {
      textLength++
    }

    truncatedHtml += char

    if (char === '>') {
      inTag = false
    }

    if (textLength >= maxLength && !inTag) {
      break
    }
  }

  // Add ellipsis
  truncatedHtml += '...'
  return { truncated: truncatedHtml, isTruncated: true }
}

type EventDetailsProps = {
  value: string
  href?: string
}

type EventCardProps = {
  eventInfo: Event
}

export const EventDetails = ({ value, href }: EventDetailsProps) => {
  const styles = css`
    ${paragraphXSmall}
    ${weightRegular}
    color: ${colors.WHITE};
    cursor: ${href ? 'pointer' : 'default'};
    text-decoration: none;
    margin: 0;
  `

  if (href) {
    return (
      <a href={href} target='_blank' rel='noopener noreferrer' css={styles}>
        {value}
      </a>
    )
  }

  return <p css={styles}>{value}</p>
}

export const EventCard = ({ eventInfo }: EventCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const imageUrl =
    getImageThumbnailId(eventInfo) ||
    '/images/generic_placeholders/placeholder_competition.png'

  const headerContent = eventInfo?.registration?.header_content
  const hasHeaderContent = headerContent && headerContent.trim().length > 0

  let truncatedContent = ''
  let isTruncated = false

  if (hasHeaderContent) {
    const result = truncateHtmlContent(headerContent, 200)
    truncatedContent = result.truncated
    isTruncated = result.isTruncated
  }

  const scheduleId = eventInfo?.registration?.root_schedule?.id
  const organizationId = eventInfo?.registration?.organization_id
  const scheduleHref = scheduleId ? `/schedule/${scheduleId}` : undefined
  const orgHref = organizationId ? `/org/${organizationId}` : undefined

  return (
    <div
      css={css`
        background-color: ${colors.NEUTRAL_700};
        padding: 16px;
        border-radius: 16px;
        margin-top: 32px;
        display: flex;
        align-items: center;
        gap: 16px;
        color: ${colors.WHITE};
        width: 100%;
        align-self: stretch;
        @media (max-width: 768px) {
          padding: 16px;
          flex-direction: column;
        }
      `}
    >
      <a
        href={scheduleHref}
        target='_blank'
        rel='noopener noreferrer'
        css={css`
          max-width: 100px;
          max-height: 100px;
          border-radius: 8px;
          border: 2px solid ${colors.WHITE};
          cursor: ${scheduleHref ? 'pointer' : 'default'};
          display: block;
          pointer-events: ${scheduleHref ? 'auto' : 'none'};
        `}
      >
        <img
          src={imageUrl}
          alt={eventInfo?.name || 'Event'}
          css={css`
            width: 100%;
            height: 100%;
          `}
          onError={(e: any) => {
            e.target.onerror = null
            e.target.src =
              '/images/generic_placeholders/placeholder_competition.png'
          }}
        />
      </a>
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        `}
      >
        <a
          css={css`
            cursor: ${scheduleHref ? 'pointer' : 'default'};
            pointer-events: ${scheduleHref ? 'auto' : 'none'};
            text-decoration: none;
          `}
          href={scheduleHref}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Text variant='heading3' color={colors.WHITE} weight='semiBold'>
            {eventInfo?.name || ''}
          </Text>
        </a>
        <EventDetails
          value={eventInfo?.registration?.organization_name || ''}
          href={orgHref}
        />
        {eventInfo?.registration?.root_schedule ? (
          <EventDetails
            value={`${dayjs(eventInfo?.registration?.root_schedule?.starts_at)
              .utc()
              .format('MMM D')} - ${dayjs(
              eventInfo?.registration?.root_schedule?.ends_at
            )
              .utc()
              .format('MMM D, YYYY')} `}
          />
        ) : null}
        {eventInfo?.registration?.location && (
          <EventDetails value={eventInfo?.location} />
        )}
      </div>

      {/* Additional Info Section */}
      {hasHeaderContent && (
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-width: 400px;
            @media (max-width: 768px) {
              max-width: 100%;
              margin-top: 16px;
            }
          `}
        >
          <div
            css={css`
              cursor: pointer;
              &:hover {
                opacity: 0.8;
              }
            `}
            onClick={handleOpenModal}
          >
            <div
              css={css`
                font-size: 14px;
                padding: 8px;
                max-height: 70vh;
                overflow-y: auto;
                color: ${colors.WHITE};
                font-family: inherit;
                line-height: 1.5;

                /* Remove default margins and excessive spacing */
                * {
                  margin: 0;
                  padding: 0;
                }

                /* Ensure first element has no top margin */
                *:first-child {
                  margin-top: 0 !important;
                }

                /* Ensure last element has no bottom margin */
                *:last-child {
                  margin-bottom: 0 !important;
                }

                /* Style the rich text content with minimal spacing */
                h1,
                h2,
                h3 {
                  margin: 16px 0 8px 0;
                  font-weight: 600;
                  color: ${colors.WHITE};
                }

                h1 {
                  font-size: 1.75em;
                }
                h2 {
                  font-size: 1.5em;
                }
                h3 {
                  font-size: 1.25em;
                }

                p {
                  margin: 8px 0;
                  color: ${colors.WHITE};
                }

                /* Force list styles - using pseudo-elements as workaround for global reset */
                ul {
                  margin: 8px 0 !important;
                  padding-left: 0 !important;
                  color: ${colors.WHITE} !important;
                  list-style: none !important;
                }

                ol {
                  margin: 8px 0 !important;
                  padding-left: 0 !important;
                  color: ${colors.WHITE} !important;
                  list-style: none !important;
                  counter-reset: list-counter !important;
                }

                ul li {
                  margin: 4px 0 !important;
                  padding-left: 28px !important;
                  color: ${colors.WHITE} !important;
                  display: block !important;
                  position: relative !important;
                }

                ol li {
                  margin: 4px 0 !important;
                  padding-left: 28px !important;
                  color: ${colors.WHITE} !important;
                  display: block !important;
                  position: relative !important;
                  counter-increment: list-counter !important;
                }

                /* Create custom bullet points and numbers */
                ul li::before {
                  content: '•' !important;
                  color: ${colors.WHITE} !important;
                  font-size: 16px !important;
                  line-height: inherit !important;
                  position: absolute !important;
                  left: 8px !important;
                  top: 0 !important;
                }

                ol li::before {
                  content: counter(list-counter) '.' !important;
                  color: ${colors.WHITE} !important;
                  font-weight: bold !important;
                  line-height: inherit !important;
                  position: absolute !important;
                  left: 8px !important;
                  top: 0 !important;
                  min-width: 20px !important;
                }

                /* Support for nested lists */
                ul ul {
                  margin: 4px 0 !important;
                  padding-left: 24px !important;
                }

                ol ol {
                  margin: 4px 0 !important;
                  padding-left: 24px !important;
                }

                ul ol,
                ol ul {
                  margin: 4px 0 !important;
                  padding-left: 24px !important;
                }

                strong {
                  font-weight: 600;
                  color: ${colors.WHITE};
                }

                em {
                  font-style: italic;
                  color: ${colors.WHITE};
                }

                u {
                  text-decoration: underline;
                  color: ${colors.WHITE};
                }

                s {
                  text-decoration: line-through;
                  color: ${colors.WHITE};
                }

                a {
                  color: ${colors.PRIMARY};
                  text-decoration: underline;
                }

                img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 4px;
                }

                blockquote {
                  border-left: 4px solid ${colors.PRIMARY};
                  padding-left: 16px;
                  margin: 12px 0;
                  font-style: italic;
                  opacity: 0.9;
                  color: ${colors.WHITE};
                }

                /* Remove empty paragraphs and BR tags that create whitespace */
                p:empty {
                  display: none;
                }

                br {
                  display: none;
                }

                /* Text alignment classes from Quill */
                .ql-align-left {
                  text-align: left;
                }
                .ql-align-center {
                  text-align: center;
                }
                .ql-align-right {
                  text-align: right;
                }
                .ql-align-justify {
                  text-align: justify;
                }
              `}
              dangerouslySetInnerHTML={{ __html: truncatedContent }}
            />
            {isTruncated && (
              <div
                css={css`
                  margin-top: 4px;
                  cursor: pointer;
                  text-align: right;
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                <Text
                  variant='paragraphSmall'
                  color={colors.PRIMARY}
                  weight='regular'
                >
                  Read More
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Info Modal */}
      {hasHeaderContent && (
        <AdditionalInfoModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          content={headerContent}
        />
      )}
    </div>
  )
}
